import {
  ArrowLeftOutlined,
  KeyOutlined,
  LoadingOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { notification } from "antd";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authSignUp } from "../../../apiservice/authService";
import { IAuthType } from "../../../apiservice/authService.type";
import useFormatApiRequest from "../../../hooks/formatApiRequest";
import {
  USER_TOKEN_KEY,
  USER_AUTH_DATA_KEY,
  USER_REFRESH_TOKEN_KEY,
} from "../../../hooks/useAuth";
import { useAppDispatch } from "../../../Redux/reduxCustomHook";
import { storePlainString, storeJSON } from "../../../utils/localStorage";
import { NotificationType } from "../../../utils/mscType.type";
import "../userAuth.css";
import PasswordInput from "../../../components/sharedcomponents/PasswordBtn/Password-input";

const UserSignUp = () => {
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [loadApi, setLoadApi] = useState(false);
  const [user, setUser] = useState<any>({});
  const [api, contextHolder] = notification.useNotification();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleInputChange = (event: any) => {
    const name = event.target.name;
    const value = event.target.value;
    setUser((values: any) => ({ ...values, [name]: value }));
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    setLoadApi(true);
    setFormLoading(true);
  };

  const result = useFormatApiRequest(
    () => authSignUp(user),
    loadApi,
    () => {
      setLoadApi(false);
    },
    () => {
      processApi();
    }
  );

  const processApi = async () => {
    if (result.httpState === "SUCCESS") {
      setFormLoading(false);
      const signinResult: IAuthType = result.data;
      storePlainString(USER_TOKEN_KEY, signinResult?.data?.accessToken || "");
      storePlainString(
        USER_REFRESH_TOKEN_KEY,
        signinResult?.data?.refreshToken || ""
      );
      storeJSON(USER_AUTH_DATA_KEY, signinResult);
      dispatch({ type: "AUTH_ADD_DATA", payload: signinResult });
      setTimeout(() => {
        navigate("/auth/verify-email");
      }, 1500);
      openNotificationWithIcon(
        "info",
        "",
        "Your Account Has Been Created",
        "#D9FFB5"
      );
    } else if (result.httpState === "ERROR") {
      setFormLoading(false);
      openNotificationWithIcon(
        "info",
        "",
        result.data?.response?.data?.errors?.[0].message ||
        result.data?.response?.data?.message ||
        result.errorMsg ||
        "Sign-up Error",
        "#FFC2B7"
      );
    }
  };

  const openNotificationWithIcon = (
    type: NotificationType,
    message: string,
    description: string,
    background?: string
  ) => {
    api[type]({
      message,
      description,
      placement: "bottomRight",
      style: { background },
    });
  };

  return (
    <>
      {contextHolder}

      <div className="w-full min-h-screen flex justify-center items-center bg-white dark:bg-stone-900">
        <div className="p-8 rounded-lg w-full max-w-lg shadow-lg bg-white dark:bg-stone-800">
          <div className="mb-2 flex items-center flex-col">
            <div className="flex items-center w-full">
              {/* Left-aligned Back Arrow */}
              <div className="flex items-center">
                <Link to="/auth">
                  <ArrowLeftOutlined className="text-lg text-blue-800 dark:text-blue-400" />
                </Link>
              </div>

              {/* Center-aligned Step Text */}
              <div className="flex w-full justify-center">
                <p className="text-black dark:text-stone-100 text-2xl font-semibold">
                  <u>Create your account</u>
                </p>
              </div>
            </div>
          </div>

          {/* Logo Section */}
          <div className="w-full  flex flex-col items-center">
            <div className="p-4">
              <img
                src={`${process.env.PUBLIC_URL + "/images/logobluebig.svg"}`}
                alt="Logo"
                className="w-32 h-auto"
              />
              <br />

              {/* <img
              src={`${process.env.PUBLIC_URL + "/images/logoblue.svg"}`}
              alt="Logo"
              className="w-32 h-auto"
            /> */}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Email Address */}
            <div className="relative w-full mb-8">
              <label className="absolute -top-3 left-3 text-slate-900 dark:text-stone-200 px-1 bg-white dark:bg-stone-800 text-sm">
                Email Address
              </label>
              <input
                required
                name="email"
                value={user?.email}
                onChange={handleInputChange}
                type="email"
                className="pl-10 pr-4 py-2 border dark:border-stone-600 rounded-lg w-full h-16 bg-transparent text-blue-800 dark:text-sky-300 placeholder-gray-500 dark:placeholder-stone-400"
                placeholder="Enter your email"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MailOutlined className="text-blue-800 dark:text-blue-400" />
              </div>
            </div>

            {/* First Name */}
            <div className="relative w-full mb-8">
              <label className="absolute -top-3 left-3 text-slate-900 dark:text-stone-200 px-1 bg-white dark:bg-stone-800 text-sm">
                First Name
              </label>
              <input
                required
                name="firstName"
                value={user?.firstName}
                onChange={handleInputChange}
                type="text"
                className="pl-10 pr-4 py-2 border dark:border-stone-600 rounded-lg w-full h-16 bg-transparent text-blue-800 dark:text-sky-300 placeholder-gray-500 dark:placeholder-stone-400"
                placeholder="Enter your first name"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <UserOutlined className="text-blue-800 dark:text-blue-400" />
              </div>
            </div>

            {/* Last Name */}
            <div className="relative w-full mb-8">
              <label className="absolute -top-3 left-3 text-slate-900 dark:text-stone-200 px-1 bg-white dark:bg-stone-800 text-sm">
                Last Name
              </label>
              <input
                required
                name="lastName"
                value={user?.lastName}
                onChange={handleInputChange}
                type="text"
                className="pl-10 pr-4 py-2 border dark:border-stone-600 rounded-lg w-full h-16 bg-transparent text-blue-800 dark:text-sky-300 placeholder-gray-500 dark:placeholder-stone-400"
                placeholder="Enter your last name"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <UserOutlined className="text-blue-800 dark:text-blue-400" />
              </div>
            </div>

            {/* Password */}
            <div className="relative w-full mb-8">
              {/* <label className="absolute -top-3 left-3 text-slate-900 dark:text-stone-200 px-1 bg-white dark:bg-stone-800 text-sm">
                Password
              </label>
              <input
                required
                name="password"
                value={user?.password}
                onChange={handleInputChange}
                type="password"
                className="pl-10 pr-4 py-2 border dark:border-stone-600 rounded-lg w-full h-16 bg-transparent text-blue-800 dark:text-sky-300 placeholder-gray-500 dark:placeholder-stone-400"
                placeholder="Enter your password"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LockOutlined className="text-blue-800 dark:text-blue-400" />
              </div> */}

              <PasswordInput
                required
                name="password"
                value={user?.password}
                onChange={handleInputChange}
                placeholder="Password"
                icon={
                  <LockOutlined className="text-blue-800 dark:text-blue-400" />
                }
              ></PasswordInput>
            </div>

            {/* Confirm Password */}
            <div className="relative w-full mb-8">
              {/* <label className="absolute -top-3 left-3 text-slate-900 dark:text-stone-200 px-1 bg-white dark:bg-stone-800 text-sm">
                Confirm Password
              </label>
              <input
                required
                name="confirmPassword"
                value={user?.confirmPassword}
                onChange={handleInputChange}
                type="password"
                className="pl-10 pr-4 py-2 border dark:border-stone-600 rounded-lg w-full h-16 bg-transparent text-blue-800 dark:text-sky-300 placeholder-gray-500 dark:placeholder-stone-400"
                placeholder="Confirm your password"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LockOutlined className="text-blue-800 dark:text-blue-400" />
              </div> */}

              <PasswordInput
                required
                name="confirmPassword"
                value={user?.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm Password"
                icon={
                  <LockOutlined className="text-blue-800 dark:text-blue-400" />
                }
              ></PasswordInput>
            </div>

            {/* Referral Code */}
            {/* <div className="relative w-full mb-8">
              <label className="absolute -top-3 left-3 text-slate-900 px-1 bg-white text-sm">
                Referral Code (Optional)
              </label>
              <input
                name="referralCode"
                value={user?.referralCode}
                onChange={handleInputChange}
                type="text"
                className="pl-10 pr-4 py-2 border rounded-lg w-full h-16 bg-transparent text-blue-800"
                placeholder="Enter your referral code"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <KeyOutlined className="text-slate-900" />
              </div>
            </div> */}

            {/* Sign Up Button */}
            <div className="mb-8">
              <button className="w-full h-16 fontAlbertSansRegular bg-blue-800 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg text-lg">
                {!formLoading ? (
                  "Continue"
                ) : (
                  <LoadingOutlined rev={undefined} />
                )}
              </button>
            </div>

            {/* Go To Login */}
            <div className="mb-8 text-center">
              <p className="text-blue-800 dark:text-blue-400">
                Already have an account?{" "}
                <Link
                  to="/auth/signin"
                  className="underline text-blue-800 dark:text-blue-400 hover:dark:text-stone-100"
                >
                  Login
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default UserSignUp;
